import { ensureNotNull } from '../helpers/assertions';
import { IDestroyable } from '../helpers/idestroyable';
import { clone, DeepPartial } from '../helpers/strict-type-checks';

import { ChartModel } from '../model/chart-model';
import { TimePoint, TimePointIndex, TimePointsRange } from '../model/time-data';
import { TimeScale, TimeScaleOptions } from '../model/time-scale';

import { Time } from './data-consumer';
import { convertTime } from './data-layer';
import { ITimeScaleApi, TimeRange } from './itime-scale-api';
import { Coordinate } from '../model/coordinate';

const enum Constants {
	AnimationDurationMs = 1000,
}

export class TimeScaleApi implements ITimeScaleApi, IDestroyable {
	private _model: ChartModel;

	public constructor(model: ChartModel) {
		this._model = model;
	}

	public destroy(): void {
		delete this._model;
	}

	public scrollPosition(): number {
		return this._timeScale().rightOffset();
	}

	public scrollToPosition(position: number, animated: boolean): void {
		if (!animated) {
			this._timeScale().setRightOffset(position);
			return;
		}

		this._timeScale().scrollToOffsetAnimated(position, Constants.AnimationDurationMs);
	}

	public scrollToRealTime(): void {
		this._timeScale().scrollToRealTime();
	}

	public getVisibleRange(): TimeRange | null {
		const visibleBars = this._timeScale().visibleBars();
		if (visibleBars === null) {
			return null;
		}

		const points = this._model.timeScale().points();
		const firstIndex = ensureNotNull(points.firstIndex());
		const lastIndex = ensureNotNull(points.lastIndex());

		return {
			from: timePointToTime(ensureNotNull(points.valueAt(Math.max(firstIndex, visibleBars.firstBar()) as TimePointIndex))),
			to: timePointToTime(ensureNotNull(points.valueAt(Math.min(lastIndex, visibleBars.lastBar()) as TimePointIndex))),
		};
	}

	public setVisibleRange(range: TimeRange): void {
		const convertedRange: TimePointsRange = {
			from: convertTime(range.from),
			to: convertTime(range.to),
		};
		this._model.setTargetTimeRange(convertedRange);
	}

	public resetTimeScale(): void {
		this._model.resetTimeScale();
	}

	public fitContent(): void {
		this._model.fitContent();
	}

	public applyOptions(options: DeepPartial<TimeScaleOptions>): void {
		this._timeScale().applyOptions(options);
	}

	public options(): Readonly<TimeScaleOptions> {
		return clone(this._timeScale().options());
	}

	public _timeScale(): TimeScale {
		return this._model.timeScale();
  }

  public timeToCoordinate(time: Time): Coordinate | null {
		const timePoint = convertTime(time);
    const timeScale = this._model.timeScale();

		const timePointIndex = timeScale.timeToIndex(timePoint, false);
		if (timePointIndex === null) {
			return null;
		}

		return timeScale.indexToCoordinate(timePointIndex);
  }

  public getDataFormTimeScale()
  {
    const timeScale = this._model.timeScale();

    return [timeScale.getPoints(), timeScale.baseIndex()];
  }

	public coordinateToTime(x: number): Time | null {
		const timeScale = this._model.timeScale();
		const timePointIndex = timeScale.coordinateToIndex(x as Coordinate);
		const timePoint = timeScale.indexToTime(timePointIndex);
		if (timePoint === null) {
			return null;
		}

		return timePoint.businessDay ?? timePoint.timestamp;
	}

public getWidth()
{
  const timeScale = this._model.timeScale();
  return timeScale.width();
}
}
function timePointToTime(point: TimePoint): Time {
	return point.businessDay || point.timestamp;
}
